import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Search, Filter, MessageCircle, Phone, Mail, Calendar,
  Eye, Plus, RefreshCw, Star, Clock, UserCheck, Activity
} from "lucide-react";
import api from "../../services/api";
import { Modal, Select, Tag, Button, Input, Card, Statistic, Row, Col, Table, Tooltip, message, Space, Badge, Empty, Typography } from "antd";

const STATUS_CONFIG = {
  nouveau: { color: "blue", label: "Nouveau" },
  contacte: { color: "orange", label: "Contacté" },
  qualifie: { color: "purple", label: "Qualifié" },
  converti: { color: "green", label: "Converti" },
  perdu: { color: "red", label: "Perdu" },
  archive: { color: "default", label: "Archivé" },
};

export default function ManagerCrm() {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 0 });
  const [filters, setFilters] = useState({ status: "", search: "", page: 1, limit: 20 });
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [commentModal, setCommentModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [statsLoading, setStatsLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/manager/crm/leads", { params: filters });
      if (res.success) {
        setLeads(res.data.leads || []);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      message.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get("/manager/crm/stats");
      if (res.success) setStats(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedLead) return;
    try {
      const res = await api.post(`/crm/leads/${selectedLead.id}/comments`, { comment: commentText });
      if (res.success) {
        message.success("Commentaire ajouté");
        setCommentText("");
        setCommentModal(false);
        loadLeadDetail(selectedLead.id);
      }
    } catch (err) {
      message.error("Erreur lors de l'ajout du commentaire");
    }
  };

  const loadLeadDetail = async (id) => {
    try {
      const res = await api.get(`/manager/crm/leads/${id}`);
      if (res.success) setSelectedLead(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const openDetail = async (lead) => {
    await loadLeadDetail(lead.id);
    setDetailModal(true);
  };

  const handleStatusChange = async (leadId, status) => {
    try {
      const res = await api.put(`/manager/crm/leads/${leadId}/status`, { status });
      if (res.success) {
        message.success("Statut mis à jour");
        loadLeads();
        loadStats();
      }
    } catch (err) {
      message.error("Erreur");
    }
  };

  const columns = [
    {
      title: "Client",
      key: "client",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-xs font-black">
            {record.prenom?.charAt(0)}{record.nom?.charAt(0)}
          </div>
          <div>
            <div className="text-sm font-bold text-white">{record.prenom} {record.nom}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1"><Phone size={10} /> {record.telephone}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (val) => <span className="text-xs text-slate-400">{val}</span>,
    },
    {
      title: "Expérience",
      dataIndex: "trading_experience",
      key: "trading_experience",
      render: (val) => (
        <Tag color={val === 'debutant' ? 'blue' : val === 'intermediaire' ? 'orange' : val === 'avance' ? 'purple' : 'green'} className="text-[10px]">
          {val || 'N/A'}
        </Tag>
      ),
    },
    {
      title: "Contact",
      dataIndex: "contact_method",
      key: "contact_method",
      render: (val) => (
        <div className="flex items-center gap-1">
          {val === 'whatsapp' ? (
            <><MessageCircle size={12} className="text-emerald-400" /> <span className="text-xs text-emerald-400">WhatsApp</span></>
          ) : (
            <><Phone size={12} className="text-indigo-400" /> <span className="text-xs text-indigo-400">Téléphone</span></>
          )}
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          style={{ width: 120 }}
          onChange={(val) => handleStatusChange(record.id, val)}
          className="status-select"
        >
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <Select.Option key={key} value={key}>
              <Badge status={cfg.color} text={cfg.label} />
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => (
        <span className="text-xs text-slate-400">{new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Contacter WhatsApp">
            <Button 
              type="text" 
              icon={<MessageCircle size={14} />} 
              onClick={() => {
                const msg = `Bonjour ${record.prenom} ! Je suis votre conseiller Ennnovatrade. Je fais suite à votre demande d'inscription. Puis-je vous aider ?`;
                window.open(`https://wa.me/${record.telephone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');
              }}
              className="text-emerald-400 hover:text-emerald-300" 
              size="small" 
            />
          </Tooltip>
          <Tooltip title="Voir détails">
            <Button type="text" icon={<Eye size={14} />} onClick={() => openDetail(record)} className="text-slate-400 hover:text-white" size="small" />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#020617] text-white p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Mes Leads CRM</h1>
            <p className="text-slate-400 text-sm mt-1">Gérez les prospects qui vous sont assignés</p>
          </div>
          <Space>
            <Button type="primary" icon={<RefreshCw size={14} />} onClick={() => { loadLeads(); loadStats(); }}>
              Actualiser
            </Button>
          </Space>
        </div>

        {/* Stats */}
        <Row gutter={[12, 12]}>
          {[
            { key: 'total', label: 'Total Leads' },
            { key: 'nouveau', label: 'Nouveaux' },
            { key: 'contacte', label: 'Contactés' },
            { key: 'qualifie', label: 'Qualifiés' },
            { key: 'converti', label: 'Convertis' },
            { key: 'today_leads', label: "Aujourd'hui" },
          ].map(({ key, label }) => (
            <Col xs={12} sm={8} md={4} key={key}>
              <Card className="bg-white/[0.02] border-white/5" size="small">
                <Statistic 
                  title={<span className="text-slate-400 text-xs font-bold uppercase tracking-wider">{label}</span>}
                  value={stats?.[key] || 0}
                  loading={statsLoading}
                  valueStyle={{ color: '#e2e8f0', fontSize: 24, fontWeight: 800 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <Input 
            placeholder="Rechercher..."
            prefix={<Search size={14} className="text-slate-500" />}
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
            className="w-72 bg-slate-900 border-white/10 text-white"
          />
          <Select
            placeholder="Statut"
            value={filters.status || undefined}
            onChange={(val) => setFilters(prev => ({ ...prev, status: val || '', page: 1 }))}
            allowClear
            className="w-36"
          >
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <Select.Option key={key} value={key}>{cfg.label}</Select.Option>
            ))}
          </Select>
        </div>

        {/* Table */}
        <Card className="bg-white/[0.02] border-white/5" bodyStyle={{ padding: 0 }}>
          <Table 
            columns={columns}
            dataSource={leads}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.page,
              total: pagination.total,
              pageSize: filters.limit,
              onChange: (page) => setFilters(prev => ({ ...prev, page })),
              showTotal: (total) => `${total} leads`,
            }}
            className="crm-manager-table"
            locale={{ emptyText: <Empty description="Aucun lead assigné" /> }}
          />
        </Card>

        {/* Detail Modal */}
        <Modal
          title={null}
          open={detailModal}
          onCancel={() => setDetailModal(false)}
          footer={null}
          width={700}
          className="crm-detail-modal"
        >
          {selectedLead && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-indigo-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-lg font-black">
                    {selectedLead.prenom?.charAt(0)}{selectedLead.nom?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white">{selectedLead.prenom} {selectedLead.nom}</h3>
                    <Badge status={STATUS_CONFIG[selectedLead.status]?.color} text={STATUS_CONFIG[selectedLead.status]?.label} />
                  </div>
                </div>
                <Button
                  type="primary"
                  icon={<MessageCircle size={14} />}
                  onClick={() => {
                    const msg = `Bonjour ${selectedLead.prenom} ! Je suis votre conseiller Ennnovatrade. Je fais suite à votre demande d'inscription. Puis-je vous aider ?`;
                    window.open(`https://wa.me/${selectedLead.telephone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  className="bg-emerald-500 hover:bg-emerald-400 border-emerald-500"
                >
                  Contacter WhatsApp
                </Button>
              </div>

              {/* Infos contact */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/[0.03] border border-white/5 rounded-xl">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Contact</div>
                  <div className="space-y-2 text-sm text-slate-300">
                    <p className="flex items-center gap-2"><Phone size={13} className="text-slate-500" /> {selectedLead.telephone}</p>
                    <p className="flex items-center gap-2"><Mail size={13} className="text-slate-500" /> {selectedLead.email}</p>
                    <p className="flex items-center gap-2"><Calendar size={13} className="text-slate-500" /> {new Date(selectedLead.created_at).toLocaleDateString('fr-FR')}</p>
                    <p className="flex items-center gap-2">
                      {selectedLead.contact_method === 'whatsapp' ? <MessageCircle size={13} className="text-emerald-400" /> : <Phone size={13} className="text-indigo-400" />}
                      {selectedLead.contact_method}
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-white/[0.03] border border-white/5 rounded-xl">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Profil</div>
                  <div className="space-y-2 text-sm text-slate-300">
                    <p><span className="text-slate-500">Expérience:</span> {selectedLead.trading_experience}</p>
                    <p><span className="text-slate-500">Domaine:</span> {selectedLead.domaine_travail || '—'}</p>
                  </div>
                </div>
              </div>

              {/* Description expérience */}
              {selectedLead.experience_description && (
                <div className="p-4 bg-white/[0.03] border border-white/5 rounded-xl">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Description de l'expérience</div>
                  <p className="text-sm text-slate-300 italic">"{selectedLead.experience_description}"</p>
                </div>
              )}

              {/* WhatsApp Message */}
              {selectedLead.whatsapp_message && (
                <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-2">
                    <MessageCircle size={12} /> Message WhatsApp envoyé au lead
                  </div>
                  <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans">{selectedLead.whatsapp_message}</pre>
                </div>
              )}

              {/* Commentaires */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Commentaires</div>
                  <Button size="small" icon={<Plus size={12} />} onClick={() => setCommentModal(true)}>Ajouter</Button>
                </div>
                {selectedLead.manager_comments?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedLead.manager_comments.map((c) => (
                      <div key={c.id} className="p-3 bg-white/[0.02] border border-white/5 rounded-xl">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-slate-300">{c.username}</span>
                          <span className="text-[10px] text-slate-600">{new Date(c.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-xs text-slate-400">{c.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-600 italic">Aucun commentaire</p>
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Comment Modal */}
        <Modal
          title="Ajouter un commentaire"
          open={commentModal}
          onCancel={() => setCommentModal(false)}
          onOk={handleAddComment}
          okText="Ajouter"
          className="crm-detail-modal"
        >
          <Input.TextArea 
            rows={3}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Écrire un commentaire sur ce lead..."
          />
        </Modal>

        <style>{`
          .crm-manager-table .ant-table-thead > tr > th {
            background: rgba(255,255,255,0.02) !important;
            border-bottom: 1px solid rgba(255,255,255,0.06) !important;
            color: #94a3b8 !important;
            font-size: 10px !important;
            font-weight: 700 !important;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            padding: 12px 16px !important;
          }
          .crm-manager-table .ant-table-tbody > tr > td {
            border-bottom: 1px solid rgba(255,255,255,0.04) !important;
            padding: 12px 16px !important;
            background: transparent !important;
          }
          .crm-manager-table .ant-table-tbody > tr:hover > td {
            background: rgba(16,185,129,0.04) !important;
          }
          .crm-manager-table .ant-table {
            background: transparent !important;
          }
          .status-select .ant-select-selector {
            background: transparent !important;
            border: 1px solid rgba(255,255,255,0.1) !important;
          }
          .crm-detail-modal .ant-modal-content {
            background: #0f172a !important;
            border: 1px solid rgba(255,255,255,0.1) !important;
          }
        `}</style>
      </div>
    </div>
  );
}
